import { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceLine, ReferenceDot, Tooltip } from "recharts";

const RANGES = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1Y", days: 365 },
  { key: "ALL", days: null },
];

const MARKET_COLOR = { ASX: "#5eead4", NYSE: "#7db3ff", NASDAQ: "#b593ff" };

export default function IndexChart({ market }) {
  const [data, setData] = useState({ series: [], meta: {} });
  const [range, setRange] = useState("ALL");
  const [hover, setHover] = useState(null);
  const [pinnedPos, setPinnedPos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get("/index", { params: { threshold: 0.6, market } })
      .then(r => setData(r.data))
      .catch(() => setData({ series: [], meta: {} }))
      .finally(() => setLoading(false));
  }, [market]);

  const filtered = useMemo(() => {
    if (!data.series?.length) return [];
    const spec = RANGES.find(r => r.key === range);
    if (!spec.days) return data.series;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - spec.days);
    return data.series.filter(p => new Date(p.date) >= cutoff);
  }, [data.series, range]);

  // positions visible in the current range, tagged with the index value on their entry_date
  const positionDots = useMemo(() => {
    if (!filtered.length || !data.positions?.length) return [];
    const valueByDate = new Map(filtered.map(p => [p.date, p.value]));
    const visibleDates = new Set(filtered.map(p => p.date));
    return data.positions
      .filter(p => visibleDates.has(p.entry_date))
      .map(p => ({ ...p, indexValue: valueByDate.get(p.entry_date) }));
  }, [data.positions, filtered]);

  const displayed = hover || filtered[filtered.length - 1] || { value: 1, date: "—", n_active: 0 };
  const baseline = filtered[0]?.value ?? 1;
  const changePct = baseline ? (displayed.value / baseline - 1) * 100 : 0;
  const isUp = changePct >= 0;
  const lineColor = isUp ? "#5eead4" : "#f38fb2";
  const gradId = isUp ? "up-grad" : "down-grad";

  return (
    <div className="glass p-6 mb-6" data-testid="index-chart">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-secondary)] font-medium">InsidEdge Conviction Index</div>
          <div className="mt-1 text-[11px] text-[color:var(--text-tertiary)]">Equal-weight of every ≥60% conviction buy · $1 baseline · adjusted close</div>
          <div className="mt-4 flex items-baseline gap-4">
            <div className="mono text-[44px] font-semibold tracking-tight leading-none" data-testid="index-value">
              ${displayed.value.toFixed(4)}
            </div>
            <div className="mono text-[15px] font-semibold" style={{ color: lineColor }} data-testid="index-change">
              {isUp ? "▲" : "▼"} {isUp ? "+" : ""}{changePct.toFixed(2)}%
            </div>
          </div>
          <div className="mt-2 mono text-[11px] text-[color:var(--text-tertiary)]" data-testid="index-hover-info">
            {displayed.date} · {displayed.n_active ?? 0} active position{displayed.n_active === 1 ? "" : "s"}
            {data.meta?.n_signals != null && (
              <span> · book of {data.meta.n_positions_priced}/{data.meta.n_signals}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-black/25 border border-white/[0.05]" data-testid="index-range">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`mono text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors ${range === r.key ? "text-[#0f1420]" : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
              style={range === r.key ? { background: "linear-gradient(135deg, var(--violet), var(--cyan))" } : {}}
            >{r.key}</button>
          ))}
        </div>
      </div>

      <div style={{ height: 220, position: "relative" }} data-testid="index-svg-wrap">
        {loading ? (
          <div className="mono text-[color:var(--text-tertiary)] terminal-loader h-full flex items-center">Fetching yfinance data</div>
        ) : filtered.length === 0 ? (
          <div className="mono text-[color:var(--text-tertiary)] h-full flex items-center justify-center">No ≥60% conviction buys yet. Adjust weights or approve borderline signals to seed the index.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={filtered} margin={{ top: 8, right: 4, bottom: 4, left: 4 }} onMouseLeave={() => setHover(null)}>
              <defs>
                <linearGradient id="up-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5eead4" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#5eead4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="down-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f38fb2" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f38fb2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis domain={["dataMin", "dataMax"]} hide />
              <ReferenceLine y={1} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" label={{ value: "$1.00 baseline", position: "insideBottomRight", fill: "#5b6382", fontSize: 10, fontFamily: "Geist Mono", offset: 6 }} />
              <Tooltip
                cursor={{ stroke: lineColor, strokeWidth: 1, strokeDasharray: "3 3" }}
                content={<IndexTooltip setHover={setHover} lineColor={lineColor} />}
              />
              <Area type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2} fill={`url(#${gradId})`} isAnimationActive={false} activeDot={{ r: 5, fill: lineColor, stroke: "#0f1420", strokeWidth: 2 }} />
              {positionDots.map((p) => (
                <ReferenceDot
                  key={p.id}
                  x={p.entry_date}
                  y={p.indexValue}
                  r={5}
                  fill="#0f1420"
                  stroke={MARKET_COLOR[p.market] || "#eef1f8"}
                  strokeWidth={2.5}
                  ifOverflow="visible"
                  isFront
                  onMouseEnter={() => setPinnedPos(p)}
                  onMouseLeave={() => setPinnedPos(null)}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
        {pinnedPos && (
          <div className="absolute pointer-events-none rounded-xl px-3 py-2 border" style={{ top: 12, right: 12, background: "rgba(15,20,32,0.95)", borderColor: "rgba(255,255,255,0.10)", backdropFilter: "blur(12px)" }} data-testid="position-dot-info">
            <div className="mono text-[10px] text-[color:var(--text-tertiary)]">SIGNAL FIRED</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`pill ${pinnedPos.market === "ASX" ? "pill-asx" : pinnedPos.market === "NYSE" ? "pill-nyse" : "pill-nasdaq"}`}>{pinnedPos.market}</span>
              <span className="mono font-semibold text-[color:var(--cyan)] text-[13px]">{pinnedPos.ticker}</span>
              <span className="mono text-[11px]" style={{ color: MARKET_COLOR[pinnedPos.market] }}>{Math.round(pinnedPos.score * 100)}</span>
            </div>
            <div className="text-[12px] mt-1">{pinnedPos.insider}</div>
            <div className="mono text-[10px] text-[color:var(--text-tertiary)] mt-1">{pinnedPos.entry_date} · entry ${pinnedPos.entry_price.toFixed(2)}</div>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-2 mono text-[11px]" data-testid="index-stats">
          <SmallStat label="PEAK" value={`$${(data.meta?.peak_value ?? 1).toFixed(4)}`} color="var(--score-high)" />
          <SmallStat label="TROUGH" value={`$${(data.meta?.trough_value ?? 1).toFixed(4)}`} color="var(--pink)" />
          <SmallStat label="TOTAL RETURN" value={`${data.meta?.total_return_pct >= 0 ? "+" : ""}${(data.meta?.total_return_pct ?? 0).toFixed(2)}%`} color={data.meta?.total_return_pct >= 0 ? "var(--score-high)" : "var(--pink)"} />
          <SmallStat label="SIGNALS TRACKED" value={data.meta?.n_positions_priced ?? 0} color="var(--cyan)" />
        </div>
      )}

      {positionDots.length > 0 && (
        <div className="mt-3 flex items-center gap-4 mono text-[10px] text-[color:var(--text-tertiary)] flex-wrap" data-testid="index-legend">
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full border-2" style={{ background: "#0f1420", borderColor: "#5eead4" }} /> ASX signal</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full border-2" style={{ background: "#0f1420", borderColor: "#7db3ff" }} /> NYSE signal</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full border-2" style={{ background: "#0f1420", borderColor: "#b593ff" }} /> NASDAQ signal</span>
          <span className="ml-auto">Hover a dot to see which insider fired the signal</span>
        </div>
      )}
    </div>
  );
}

function SmallStat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-black/25 border border-white/[0.04] px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium">{label}</div>
      <div className="mono text-[13px] font-semibold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function IndexTooltip({ active, payload, setHover, lineColor }) {
  if (active && payload && payload.length) {
    const p = payload[0].payload;
    setTimeout(() => setHover(p), 0);
    return (
      <div className="rounded-xl px-3 py-2 border" style={{ background: "rgba(15,20,32,0.95)", borderColor: "rgba(255,255,255,0.10)", backdropFilter: "blur(12px)" }}>
        <div className="mono text-[11px] text-[color:var(--text-tertiary)]">{p.date}</div>
        <div className="mono text-[15px] font-semibold" style={{ color: lineColor }}>${p.value.toFixed(4)}</div>
        <div className="mono text-[10px] text-[color:var(--text-tertiary)] mt-0.5">{p.n_active} active</div>
      </div>
    );
  }
  return null;
}
